import React, { useState, useCallback, useMemo } from 'react';
import './setupPdfWorker.js';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

/**
 * PDF avec texte sélectionnable (calques), pagination locale.
 *
 * `fileData` est l'ArrayBuffer du livre, téléchargé par l'appelant AVEC l'en-tête
 * d'authentification (react-pdf, à qui l'on passait l'URL, la chargeait sans jeton).
 */
export default function PdfViewer({ fileData, onSelectionChange, className }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  // pdf.js TRANSFÈRE le tampon à son worker, ce qui le vide côté page : on lui
  // remet une copie, pour qu'un remontage puisse rouvrir le même fichier. L'objet
  // est mémoïsé — un `file` recréé à chaque rendu rechargerait le document.
  const file = useMemo(
    () => (fileData ? { data: new Uint8Array(fileData.slice(0)) } : null),
    [fileData],
  );

  const onDocumentLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setPageNumber(1);
  }, []);

  const handleMouseUp = useCallback(() => {
    requestAnimationFrame(() => {
      const sel = window.getSelection();
      const text = sel?.toString().trim() || '';
      if (!text || !sel.rangeCount) {
        onSelectionChange?.({ text: '', x: 0, y: 0, pageContext: null });
        return;
      }
      try {
        const rect = sel.getRangeAt(0).getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top;
        onSelectionChange?.({
          text,
          x,
          y,
          pageContext: {
            type: 'pdf',
            page: pageNumber,
            numPages: numPages ?? undefined,
          },
        });
      } catch {
        onSelectionChange?.({ text: '', x: 0, y: 0, pageContext: null });
      }
    });
  }, [onSelectionChange, pageNumber, numPages]);

  const goPrev = () => setPageNumber((p) => Math.max(1, p - 1));
  const goNext = () =>
    setPageNumber((p) => (numPages ? Math.min(numPages, p + 1) : p));

  return (
    <div
      className={`pdf-viewer-wrap ${className || ''}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        alignItems: 'center',
      }}
    >
      <div className="pdf-viewer-toolbar">
        <button
          type="button"
          onClick={goPrev}
          disabled={pageNumber <= 1}
        >
          Page précédente
        </button>
        <span className="pdf-viewer-page-label">
          {numPages ? `${pageNumber} / ${numPages}` : '…'}
        </span>
        <button
          type="button"
          onClick={goNext}
          disabled={!numPages || pageNumber >= numPages}
        >
          Page suivante
        </button>
      </div>
      <div
        className="pdf-viewer-scroll"
        onMouseUp={handleMouseUp}
        style={{
          flex: 1,
          overflow: 'auto',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Document
          file={file}
          loading={<div className="pdf-viewer-loading">Chargement du PDF…</div>}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={(e) => console.error('PDF:', e)}
        >
          <Page
            pageNumber={pageNumber}
            width={Math.min(900, typeof window !== 'undefined' ? window.innerWidth - 48 : 800)}
            renderTextLayer
            renderAnnotationLayer
          />
        </Document>
      </div>
    </div>
  );
}
